/**
 * Subscription Sidebar Popup — isolated from email-subscription-popup-alter
 */

// Dev preview: set false before release
const SSP_DEV_PREVIEW_THANKYOU = false;
// subscription | sms | thank-you | thank-you-member | thank-you-sms
const SSP_DEV_PREVIEW_STEP = "thank-you-member";

function registerSubscriptionSidebarPopup() {
  if (!window.Alpine || window.__sspAlpineRegistered) return false;

  const root = document.getElementById("subscription-sidebar-popup");
  if (!root) return false;

  if (!window.theme?.emailSubscription) {
    console.warn("[SSP] theme.emailSubscription is missing");
    return false;
  }

  const { apiUrl, acceptedLanguage, apiKey, xAppid } = theme.emailSubscription;
  const {
    customerTags,
    customerEmail,
    popupDelayDays,
    popupDelaySeconds,
    popupSubscribed,
    autoShowPopup,
    enableSms,
    enableRegistration,
    scrollIdleMs,
    storagePrefix,
  } = root.dataset;

  const LS_DELAY = `${storagePrefix}_delay_date`;
  const LS_TAB_DISMISSED = `${storagePrefix}_tab_dismissed`;

  Alpine.data("subscriptionSidebarPopup", () => ({
    subscriptionBtnText: "",
    showTriggerTab: true,
    showPopup: SSP_DEV_PREVIEW_THANKYOU,
    tabHiddenByScroll: false,
    showStep: SSP_DEV_PREVIEW_THANKYOU ? SSP_DEV_PREVIEW_STEP : "subscription",
    email: "",
    phoneCode: "+1",
    phoneNumber: "",
    disclaimerChecked: false,
    smsDisclaimerChecked: false,
    errMsg: "",
    loading: false,
    popupDelayDate: null,
    isLogin: false,
    isSubscribed: false,
    isSmsSubscribed: false,
    isMember: false,
    enableSms: true,
    enableRegistration: true,
    scrollIdleTimer: null,
    lastScrollY: 0,
    shouldPageActive: {
      subscription: true,
      sms: true,
      "thank-you": true,
      "thank-you-member": true,
      "thank-you-sms": true,
    },

    dismissTab() {
      this.showPopup = false;
      this.showTriggerTab = false;
      localStorage.setItem(LS_TAB_DISMISSED, "true");
    },

    togglePopup() {
      this.showPopup = !this.showPopup;
      if (!this.showPopup) {
        const targetDay = new Date();
        targetDay.setHours(0, 0, 0, 0);
        targetDay.setDate(new Date().getDate() + this.popupDelayDate);
        localStorage.setItem(LS_DELAY, targetDay);

        this.resetActivePage();
        this.setStep("subscription");
        this.email = customerEmail ? customerEmail : "";
        this.errMsg = "";
      }
    },

    resetActivePage() {
      if (this.isLogin) {
        this.shouldPageActive = {
          subscription: !this.isSubscribed,
          sms: !this.isSmsSubscribed && this.enableSms,
          "thank-you": !this.isMember && this.enableRegistration,
          "thank-you-member": true,
          "thank-you-sms": true,
        };
        return;
      }
      this.shouldPageActive = {
        subscription: true,
        sms: true,
        "thank-you": true,
        "thank-you-member": true,
        "thank-you-sms": true,
      };
    },

    setStep(step) {
      this.showStep = step;
      if (SSP_DEV_PREVIEW_THANKYOU) return;
      if (step.indexOf("thank-you") > -1) {
        const closeEl = document.getElementById("ssp_thank_you_close_popup");
        const delayEl = document.getElementById("ssp_thank_you_close_popup_delay");
        if (closeEl && delayEl && closeEl.value === "true" && delayEl.value) {
          setTimeout(() => this.togglePopup(), Number(delayEl.value));
        }
      }
    },

    getActiveStep(step) {
      const steps = Object.keys(this.shouldPageActive);
      let index = steps.indexOf(step);
      while (index < steps.length) {
        if (this.shouldPageActive[steps[index]]) return steps[index];
        index++;
      }
      return null;
    },

    goNextStep(expectedNextStep) {
      this.setStep(this.getActiveStep(expectedNextStep));
    },

    getUsageOptionValues() {
      const values = [];
      root.querySelectorAll(".ssp__tag input[type='checkbox']").forEach((item) => {
        if (item.checked) values.push(parseInt(item.value, 10));
      });
      return values;
    },

    async submitEmailForm() {
      this.errMsg = "";
      const isEmailValid = validateEmail(this.email);

      if (this.email === "") {
        this.errMsg = subscriptionSidebarPopupSettingJsons.isnull_email;
        return;
      }
      if (!isEmailValid) {
        this.errMsg = subscriptionSidebarPopupSettingJsons.check_email;
        return;
      }
      if (!this.disclaimerChecked) {
        this.errMsg = subscriptionSidebarPopupSettingJsons.check_checkbox;
        return;
      }

      const { data } = await this.fetchCustomerInfo(this.email);
      this.shouldPageActive = {
        ...this.shouldPageActive,
        sms: !data.acceptsMarketingSms && this.enableSms,
        "thank-you": !data.registerUser && this.enableRegistration,
      };

      const fieldsArray = createFieldsArray(subscriptionSidebarPopupSettingJsons);
      const usageOption = document.getElementById("ssp_usage_option")?.value;
      if (usageOption) {
        fieldsArray.push({
          id: usageOption,
          type: "multiple",
          value: this.getUsageOptionValues(),
          overwrite: false,
        });
      }

      const contactListId = getContactListId(subscriptionSidebarPopupSettingJsons);
      const subscribeInfoCookie = window.getCookieForEmarsys("subscribe_info");
      let eventTracking = null;

      if (subscribeInfoCookie) {
        try {
          eventTracking = JSON.parse(decodeURIComponent(subscribeInfoCookie));
          delete eventTracking.ts;
        } catch (error) {
          console.warn("Failed to parse subscribe_info cookie:", error);
        }
      }

      const apiBody = {
        email: this.email,
        contactListId,
        smsContactListId: subscriptionSidebarPopupSettingJsons.contact_list_id_sms,
        fields: fieldsArray,
        ...(eventTracking && { eventTracking }),
      };

      try {
        this.loading = true;
        const response = await fetch(apiUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            apiKey,
            traceId,
            "X-appid": xAppid,
            "Accepted-Language": acceptedLanguage,
          },
          body: JSON.stringify(apiBody),
        });
        const result = await response.json();

        if (result.message === "Success") {
          this.goNextStep("sms");
          if (this.email === customerEmail) {
            this.shouldPageActive.subscription = false;
            this.isSubscribed = true;
          }
        } else {
          this.errMsg = subscriptionSidebarPopupSettingJsons.other_msg;
        }
      } catch (error) {
        this.errMsg = subscriptionSidebarPopupSettingJsons.other_msg;
        console.log(error);
      } finally {
        this.loading = false;
      }
    },

    async submitSmsForm() {
      this.errMsg = "";
      const phone = `${this.phoneCode}${this.phoneNumber}`;

      const isPhoneNumberValid = () => {
        const phoneNumberRegex = /^(?=.{9,15}$)\+\d(-?\d)*$/;
        const containOnlyOneOrNoHyphen = phone.split("-").length - 1 <= 1;
        return phoneNumberRegex.test(phone) && containOnlyOneOrNoHyphen;
      };

      if (!this.smsDisclaimerChecked) {
        this.errMsg = "Please tick agreement box to proceed.";
        return;
      }
      if (this.phoneNumber === "") {
        this.errMsg = "Please enter your phone number.";
        return;
      }
      if (!isPhoneNumberValid(phone)) {
        this.errMsg = "Please enter a valid phone number";
        return;
      }

      const {
        s_id_sms,
        s_value_sms,
        s_overwrite_sms,
        t_id_sms,
        t_value_sms,
        t_overwrite_sms,
        m_id_sms,
        m_value_sms,
        m_overwrite_sms,
        m_2_id_sms,
        m_2_value_sms,
        m_2_overwrite_sms,
        contact_list_id_sms,
      } = subscriptionSidebarPopupSettingJsons;

      let fieldsArray = createFieldsArray(subscriptionSidebarPopupSettingJsons);
      const contactListId = getContactListId(subscriptionSidebarPopupSettingJsons);
      fieldsArray = [
        ...fieldsArray,
        ...(s_id_sms
          ? [{ id: s_id_sms, type: "single", value: Number(s_value_sms), overwrite: JSON.parse(s_overwrite_sms) }]
          : []),
        ...(t_id_sms
          ? [{ id: t_id_sms, type: "text", value: Number(t_value_sms), overwrite: JSON.parse(t_overwrite_sms) }]
          : []),
        ...(m_id_sms
          ? [{ id: m_id_sms, type: "multiple", value: m_value_sms.split(",").map((item) => Number(item)), overwrite: JSON.parse(m_overwrite_sms) }]
          : []),
        ...(m_2_id_sms
          ? [{ id: m_2_id_sms, type: "multiple", value: m_2_value_sms.split(",").map((item) => Number(item)), overwrite: JSON.parse(m_2_overwrite_sms) }]
          : []),
      ];

      const subscribeInfoCookie = window.getCookieForEmarsys("subscribe_info");
      let eventTracking = null;
      if (subscribeInfoCookie) {
        try {
          eventTracking = JSON.parse(decodeURIComponent(subscribeInfoCookie));
          delete eventTracking.ts;
        } catch (error) {
          console.warn("Failed to parse subscribe_info cookie:", error);
        }
      }

      const apiBody = {
        email: this.email === "" ? customerEmail : this.email,
        phone,
        contactListId,
        smsContactListId: contact_list_id_sms,
        fields: fieldsArray,
        ...(eventTracking && { eventTracking }),
      };

      try {
        this.loading = true;
        const checkPhoneRegistered = await this.fetchCustomerInfo(
          this.email === "" ? customerEmail : this.email,
          encodeURIComponent(phone)
        );

        if (checkPhoneRegistered.data.registerPhone) {
          this.errMsg = "This phone number is already registered.";
          return;
        }

        const response = await fetch(apiUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            apiKey,
            traceId,
            "X-appid": xAppid,
            "Accepted-Language": acceptedLanguage,
          },
          body: JSON.stringify(apiBody),
        });
        const result = await response.json();

        if (result.message === "Success") {
          const shouldSmsPageActive = this.email === "" ? false : this.email !== customerEmail;
          this.shouldPageActive = {
            ...this.shouldPageActive,
            sms: shouldSmsPageActive,
            "thank-you": !this.isMember && this.enableRegistration,
            "thank-you-member": false,
          };
          this.isSmsSubscribed = !shouldSmsPageActive;
          this.goNextStep("thank-you");
        } else {
          this.errMsg = subscriptionSidebarPopupSettingJsons.other_msg;
        }
      } catch (error) {
        this.errMsg = subscriptionSidebarPopupSettingJsons.other_msg;
        console.log(error);
      } finally {
        this.loading = false;
      }
    },

    async fetchCustomerInfo(email, phone) {
      try {
        this.loading = true;
        const phoneQuery = phone ? `&phone=${phone}` : "";
        const response = await fetch(
          `https://api-a.ecoflow.com/website/subscribe/searchSubInfo?email=${email}${phoneQuery}`,
          { method: "GET", headers: { "X-appid": xAppid } }
        );
        return await response.json();
      } catch (error) {
        throw new Error(error);
      } finally {
        this.loading = false;
      }
    },

    parseBool(value, fallback) {
      if (value === undefined || value === null || value === "") return fallback;
      try {
        return JSON.parse(value);
      } catch (error) {
        return fallback;
      }
    },

    initScrollBehavior() {
      const idleMs = Number(scrollIdleMs) || 2000;
      const trigger = root.querySelector(".ssp__trigger");
      if (!trigger) return;

      const onScroll = () => {
        const currentY = window.scrollY;
        if (Math.abs(currentY - this.lastScrollY) > 5) {
          this.tabHiddenByScroll = true;
          trigger.classList.add("ssp--hidden");
        }
        this.lastScrollY = currentY;

        clearTimeout(this.scrollIdleTimer);
        this.scrollIdleTimer = setTimeout(() => {
          if (this.showTriggerTab && !this.showPopup) {
            this.tabHiddenByScroll = false;
            trigger.classList.remove("ssp--hidden");
          }
        }, idleMs);
      };

      window.addEventListener("scroll", onScroll, { passive: true });
    },

    init() {
      this.subscriptionBtnText = root.dataset.subscriptionBtnText || "Subscribe";
      this.email = customerEmail ? customerEmail : "";
      this.isLogin = !!customerEmail;
      this.isMember = !!customerEmail;
      this.enableSms = this.parseBool(enableSms, false);
      this.enableRegistration = this.parseBool(enableRegistration, false);
      this.popupDelayDate = Number(popupDelayDays) || 0;

      this.shouldPageActive = {
        ...this.shouldPageActive,
        sms: this.enableSms,
        "thank-you": this.enableRegistration,
      };

      const tabDismissed = localStorage.getItem(LS_TAB_DISMISSED) === "true";
      if (tabDismissed && !SSP_DEV_PREVIEW_THANKYOU) {
        this.showTriggerTab = false;
        return;
      }

      this.showTriggerTab = true;

      if (!SSP_DEV_PREVIEW_THANKYOU && customerEmail) {
        const fetchCustomerInformation = async () => {
          try {
            const data = await this.fetchCustomerInfo(customerEmail);
            if (data.message === "Success") {
              const { acceptsMarketing } = data.data;
              if (acceptsMarketing) this.subscriptionBtnText = "Submit";
              if (!this.enableSms) this.shouldPageActive.sms = false;
              if (!this.enableRegistration) this.shouldPageActive["thank-you"] = false;
              this.goNextStep("subscription");
            }
          } catch (error) {
            console.warn("[SSP] fetchCustomerInfo failed:", error);
          }
        };
        fetchCustomerInformation();
      }

      const now = new Date();
      const delayDate = localStorage.getItem(LS_DELAY);
      const isDelayDateValid = delayDate && now.getTime() < new Date(delayDate).getTime();
      const parsedAutoShowPopup = this.parseBool(autoShowPopup, false);

      if (!this.isSubscribed && !isDelayDateValid && parsedAutoShowPopup) {
        setTimeout(() => {
          this.showPopup = true;
        }, Number(popupDelaySeconds) * 1000);
      }

      this.initScrollBehavior();
    },

    phoneCodeList: [
      "+1", "+7", "+20", "+27", "+30", "+31", "+32", "+33", "+34", "+36", "+39", "+40", "+41", "+43", "+44", "+45",
      "+46", "+47", "+48", "+49", "+51", "+52", "+53", "+54", "+55", "+56", "+57", "+58", "+60", "+61", "+62", "+63",
      "+64", "+65", "+66", "+81", "+82", "+84", "+86", "+90", "+91", "+92", "+94", "+95", "+98", "+211", "+212", "+213",
      "+216", "+218", "+220", "+221", "+222", "+223", "+224", "+225", "+226", "+227", "+228", "+229", "+230", "+231",
      "+232", "+233", "+234", "+235", "+236", "+237", "+238", "+239", "+240", "+241", "+242", "+243", "+244", "+245",
      "+246", "+248", "+249", "+250", "+251", "+252", "+253", "+254", "+255", "+256", "+257", "+258", "+260", "+261",
      "+262", "+263", "+264", "+265", "+266", "+267", "+268", "+269", "+290", "+291", "+297", "+298", "+299", "+350",
      "+351", "+352", "+353", "+354", "+355", "+356", "+357", "+358", "+359", "+370", "+371", "+372", "+373", "+374",
      "+375", "+376", "+377", "+378", "+379", "+380", "+381", "+382", "+383", "+385", "+386", "+387", "+389", "+420",
      "+421", "+423", "+500", "+501", "+502", "+503", "+504", "+505", "+506", "+507", "+508", "+509", "+590", "+591",
      "+592", "+593", "+594", "+595", "+596", "+597", "+598", "+599", "+670", "+672", "+673", "+674", "+675", "+676",
      "+677", "+678", "+679", "+680", "+681", "+682", "+683", "+685", "+686", "+687", "+688", "+689", "+690", "+691",
      "+692", "+850", "+852", "+853", "+855", "+856", "+870", "+880", "+886", "+960", "+961", "+962", "+963", "+964",
      "+965", "+966", "+967", "+968", "+970", "+971", "+972", "+973", "+974", "+975", "+976", "+977", "+992", "+993",
      "+994", "+995", "+996", "+998",
    ],
  }));

  window.__sspAlpineRegistered = true;
  return true;
}

function bootSubscriptionSidebarPopup() {
  if (!registerSubscriptionSidebarPopup()) return;

  const root = document.getElementById("subscription-sidebar-popup");
  if (!root || typeof Alpine.initTree !== "function") return;

  if (root._x_dataStack?.length && typeof Alpine.destroyTree === "function") {
    Alpine.destroyTree(root);
  }

  Alpine.initTree(root);
}

document.addEventListener("alpine:init", bootSubscriptionSidebarPopup);

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", bootSubscriptionSidebarPopup);
} else {
  bootSubscriptionSidebarPopup();
}
